import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Receipt,
  BarChart3,
  TrendingUp,
  BookOpen,
  Search,
  Package,
  Settings,
  LogOut,
  ArrowLeft,
  Sun,
  Moon,
  RefreshCw
} from 'lucide-react';
import AccountSelector from './AccountSelector';
import useTheme from '../hooks/useTheme';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/cost', label: 'Costos', icon: Receipt },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/profitability', label: 'Rentabilidad', icon: TrendingUp },
  { path: '/publications', label: 'Publicaciones', icon: BookOpen },
  { path: '/reindexado', label: 'Reindexado ML', icon: RefreshCw },
  { path: '/catalog', label: 'Catálogo ML', icon: BookOpen },
  { path: '/research', label: 'Busq. Productos', icon: Search },
  { path: '/inventory', label: 'Inventario', icon: Package, badge: 'Próximamente' },
  { path: '/settings', label: 'Configuración', icon: Settings },
];

export default function DashboardLayout({ children, title, subtitle, icon: TitleIcon, backTo, headerRight }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();

  const userData = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('meli_user') || '{}');
    } catch { return {}; }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('meli_token');
    localStorage.removeItem('meli_user');
    navigate('/login');
  };

  const displayName = userData.name || userData.email?.split('@')[0] || 'Usuario';

  return (
    <div className="min-h-screen bg-[#0B1120]">
      {/* Navbar */}
      <nav data-testid="main-navbar" className="sticky top-0 z-50 border-b border-slate-800/50 bg-[#0B1120]/90 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-4 flex items-center h-14">
          {/* Logo */}
          <NavLink to="/dashboard" data-testid="nav-logo" className="text-lg font-bold text-white mr-8 shrink-0 tracking-wide">
            MeLi Pro Analytics
          </NavLink>

          {/* Nav items */}
          <div className="flex items-center gap-0.5 overflow-x-auto hide-scrollbar flex-1">
            {navItems.map(({ path, label, icon: Icon, badge }) => {
              const isActive = location.pathname === path;
              return (
                <NavLink
                  key={path}
                  to={path}
                  data-testid={`nav-${path.replace('/', '')}`}
                  className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium whitespace-nowrap transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-600/20 text-blue-400'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                  }`}
                >
                  <Icon size={15} />
                  <span>{label}</span>
                  {badge && (
                    <span className="absolute -top-1 -right-1 text-[8px] bg-amber-500/20 text-amber-400 px-1 rounded font-medium">
                      {badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>

          {/* Account + User + Logout */}
          <div className="flex items-center gap-3 ml-4 shrink-0">
            {/* In the navbar so every screen is scoped to the same account:
                a selector living on one page leaves the rest showing another
                brand's numbers. */}
            <AccountSelector
              userData={userData}
              onAccountChange={() => window.location.reload()}
            />
            <span data-testid="user-display-name" className="text-sm text-slate-300 font-medium hidden lg:block">
              {displayName}
            </span>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              title="Cerrar sesión"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </nav>

      {/* Page Header (optional) */}
      {title && (
        <div className="max-w-[1600px] mx-auto px-6 pt-6 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {backTo && (
                <button
                  data-testid="back-button"
                  onClick={() => navigate(backTo)}
                  className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              {TitleIcon && <TitleIcon size={28} className="text-slate-300" />}
              <div>
                <h1 data-testid="page-title" className="text-2xl font-bold text-white">{title}</h1>
                {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
              </div>
            </div>
            {headerRight && <div className="flex items-center gap-2">{headerRight}</div>}
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-4">
        {children}
      </main>

      {/* Theme Toggle (bottom left) */}
      <div className="fixed bottom-6 left-6 z-50">
        <button
          data-testid="theme-toggle"
          onClick={toggleTheme}
          className="flex items-center gap-1 bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 rounded-full p-1.5 shadow-lg"
        >
          <div className={`p-1.5 rounded-full transition-colors ${!isDark ? 'bg-blue-600 text-white' : 'text-slate-400'}`}>
            <Sun size={14} />
          </div>
          <div className={`p-1.5 rounded-full transition-colors ${isDark ? 'bg-blue-600 text-white' : 'text-slate-400'}`}>
            <Moon size={14} />
          </div>
        </button>
      </div>
    </div>
  );
}
